import React, { useState } from 'react';
import { Table, TableColumnsType, Button, Space, Modal, Drawer, Descriptions, message } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import FilterArea, { FilterOptions, DEFAULT_FILTER_OPTIONS } from './components/FilterArea';
import {
  GET_ADDRESS_INVALID_LIST,
  DELETE_ADDRESS_INVALID,
  GetAddressInvalidListResponse,
  AddressInvalidType,
} from './graphql/addressInvalidGraphql';

const AddressInvalidPage: React.FC = () => {
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [appliedFilters, setAppliedFilters] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [detailRecord, setDetailRecord] = useState<AddressInvalidType | null>(null);
  const { t } = useTranslation();

  const { data, loading, refetch } = useQuery<GetAddressInvalidListResponse>(
    GET_ADDRESS_INVALID_LIST,
    {
      variables: {
        addressHash: appliedFilters.addressHash || undefined,
        normalizedStreet: appliedFilters.normalizedStreet || undefined,
        normalizedUnit: appliedFilters.normalizedUnit || undefined,
        zipcode: appliedFilters.zipcode || undefined,
        page: currentPage,
        size: pageSize,
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const [deleteInvalid] = useMutation(DELETE_ADDRESS_INVALID, {
    onCompleted: () => refetch(),
    onError: (err) => message.error(err.message),
  });

  const handleFilter = () => {
    setAppliedFilters({ ...filterOptions });
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilterOptions(DEFAULT_FILTER_OPTIONS);
    setAppliedFilters(DEFAULT_FILTER_OPTIONS);
    setCurrentPage(1);
  };

  const handleTableChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const showDeleteConfirm = (record: AddressInvalidType) => {
    Modal.confirm({
      title: t('mapTool.addressInvalid.deleteTitle') as string,
      content: t('mapTool.addressInvalid.deleteContent') as string,
      okText: t('components.button.confirm') as string,
      okType: 'danger',
      cancelText: t('components.button.cancel') as string,
      onOk: () => deleteInvalid({ variables: { id: Number(record.id) } }),
    });
  };

  const formatDate = (v: string | null | undefined) =>
    v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '—';

  const columns: TableColumnsType<AddressInvalidType> = [
    {
      title: t('mapTool.addressInvalid.table.id'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center',
    },
    {
      title: t('mapTool.addressInvalid.table.normalizedStreet'),
      dataIndex: 'normalizedStreet',
      key: 'normalizedStreet',
      width: 280,
      ellipsis: true,
    },
    {
      title: t('mapTool.addressInvalid.table.normalizedUnit'),
      dataIndex: 'normalizedUnit',
      key: 'normalizedUnit',
      width: 100,
      align: 'center',
      render: (v: string | null | undefined) => v ?? '—',
    },
    {
      title: t('mapTool.addressInvalid.table.city'),
      dataIndex: 'city',
      key: 'city',
      width: 130,
      ellipsis: true,
    },
    {
      title: t('mapTool.addressInvalid.table.state'),
      dataIndex: 'state',
      key: 'state',
      width: 70,
      align: 'center',
    },
    {
      title: t('mapTool.addressInvalid.table.zipcode'),
      dataIndex: 'zipcode',
      key: 'zipcode',
      width: 90,
      align: 'center',
    },
    {
      title: t('mapTool.addressInvalid.table.invalidReason'),
      dataIndex: 'invalidReason',
      key: 'invalidReason',
      width: 160,
      align: 'center',
    },
    {
      title: t('mapTool.addressInvalid.table.apiSource'),
      dataIndex: 'apiSource',
      key: 'apiSource',
      width: 140,
      align: 'center',
      render: (v: string | null | undefined) => v ?? '—',
    },
    {
      title: t('mapTool.addressInvalid.table.checkCount'),
      dataIndex: 'checkCount',
      key: 'checkCount',
      width: 90,
      align: 'center',
    },
    {
      title: t('mapTool.addressInvalid.table.hitCount'),
      dataIndex: 'hitCount',
      key: 'hitCount',
      width: 80,
      align: 'center',
    },
    {
      title: t('mapTool.addressInvalid.table.retryAfter'),
      dataIndex: 'retryAfter',
      key: 'retryAfter',
      width: 180,
      align: 'center',
      render: formatDate,
    },
    {
      title: t('mapTool.addressInvalid.table.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      align: 'center',
      render: formatDate,
    },
    {
      title: t('components.button.action') as string,
      key: 'action',
      width: 160,
      fixed: 'right' as const,
      align: 'center',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            size="small"
            onClick={() => setDetailRecord(record)}
          >
            {t('components.button.view')}
          </Button>
          <Button
            type="link"
            size="small"
            danger
            onClick={() => showDeleteConfirm(record)}
          >
            {t('components.button.delete')}
          </Button>
        </Space>
      ),
    },
  ];

  const pageData = data?.addressInvalidList;

  return (
    <div className="p-6">
      <h4 className="text-xl font-bold mb-4">{t('mapTool.addressInvalid.pageTitle')}</h4>

      <FilterArea filters={filterOptions} setFilters={setFilterOptions} />

      <div className="flex justify-end items-center mb-4 mt-4">
        <div className="flex items-center gap-2">
          <Button type="primary" onClick={handleFilter} className="w-auto min-w-24">
            <span className="font-bold">{t('components.button.filter')}</span>
          </Button>
          <Button onClick={handleResetFilters} className="w-auto min-w-24">
            <span className="font-bold">{t('components.button.reset')}</span>
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <Table
          columns={columns}
          dataSource={pageData?.content || []}
          loading={loading}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: pageData?.totalElements || 0,
            showSizeChanger: true,
            showTotal: (total) =>
              t('mapTool.addressInvalid.table.paginationTotal', { total }),
            onChange: handleTableChange,
            onShowSizeChange: handleTableChange,
          }}
        />
      </div>

      <Drawer
        title={t('mapTool.addressInvalid.detailTitle')}
        open={!!detailRecord}
        onClose={() => setDetailRecord(null)}
        width={800}
        placement="right"
      >
        {detailRecord && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label={t('mapTool.addressInvalid.detail.addressHash')}>
              <span style={{ wordBreak: 'break-all' }}>{detailRecord.addressHash}</span>
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressInvalid.detail.normalizedStreet')}>
              {detailRecord.normalizedStreet}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressInvalid.detail.normalizedUnit')}>
              {detailRecord.normalizedUnit ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressInvalid.detail.city')}>
              {detailRecord.city}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressInvalid.detail.state')}>
              {detailRecord.state}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressInvalid.detail.zipcode')}>
              {detailRecord.zipcode}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressInvalid.detail.invalidReason')}>
              {detailRecord.invalidReason}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressInvalid.detail.dpvMatchCode')}>
              {detailRecord.dpvMatchCode ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressInvalid.detail.apiSource')}>
              {detailRecord.apiSource ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressInvalid.detail.firstSeenAt')}>
              {formatDate(detailRecord.firstSeenAt)}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressInvalid.detail.lastCheckedAt')}>
              {formatDate(detailRecord.lastCheckedAt)}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressInvalid.detail.retryAfter')}>
              {formatDate(detailRecord.retryAfter)}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressInvalid.detail.checkCount')}>
              {detailRecord.checkCount}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressInvalid.detail.hitCount')}>
              {detailRecord.hitCount}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressInvalid.detail.createdAt')}>
              {formatDate(detailRecord.createdAt)}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressInvalid.detail.updatedAt')}>
              {formatDate(detailRecord.updatedAt)}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </div>
  );
};

export default AddressInvalidPage;